import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Users, Shield, UserCheck, UserX, Clock, Search, RefreshCw, Settings, Activity, Eye, EyeOff, GitMerge, RotateCcw,
} from "lucide-react";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";

const roleLabels = {
  admin: "Administrador",
  rd_lead: "Líder I+D",
  open_innovation_lead: "Líder Open Innovation",
  analyst: "Analista",
  stakeholder: "Stakeholder",
  viewer: "Visualizador",
  internship: "Internship",
};

const roleBadgeColors = {
  admin: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  rd_lead: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  open_innovation_lead: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  analyst: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  stakeholder: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300",
  viewer: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
  internship: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
};

const allPages = [
  { id: "portfolio", label: "Portfolio" },
  { id: "gantt", label: "Gantt" },
  { id: "heatmap", label: "Heatmap" },
  { id: "pilots", label: "Pilotos" },
  { id: "projects", label: "Proyectos I+D" },
  { id: "grants", label: "Subvenciones" },
  { id: "partners", label: "Partners" },
  { id: "timesheets", label: "Timesheets" },
  { id: "finance", label: "Finanzas" },
  { id: "kpis", label: "KPIs" },
  { id: "calendar", label: "Calendario" },
  { id: "reports", label: "Reporting" },
];

export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewerDialogOpen, setViewerDialogOpen] = useState(false);
  const [viewerPermissions, setViewerPermissions] = useState({
    allowed_pages: ["portfolio", "gantt", "calendar"],
    can_view_financials: false,
    can_view_partners: true,
    can_view_kpis: true,
    can_export_data: false,
  });
  const [newRole, setNewRole] = useState("");
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approvalRole, setApprovalRole] = useState("analyst");
  const [syncStatus, setSyncStatus] = useState(null);
  const [initiativeMerges, setInitiativeMerges] = useState([]);
  const [mergesLoading, setMergesLoading] = useState(true);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const { token, user } = useAuth();

  const fetchMerges = async () => {
    try {
      const data = await api.listInitiativeMerges(token);
      setInitiativeMerges(data);
    } catch (error) {
      toast.error("Error al cargar fusiones de iniciativas");
    } finally {
      setMergesLoading(false);
    }
  };

  const confirmRestoreMerge = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const res = await api.restoreInitiativeMerge(token, restoreTarget.id);
      toast.success(res.note ? `Iniciativa restaurada. ${res.note}` : "Iniciativa restaurada");
      fetchMerges();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error al restaurar");
    } finally {
      setRestoring(false);
      setRestoreTarget(null);
    }
  };

  const fetchUsers = async () => {
    try {
      const [allUsers, pending] = await Promise.all([
        api.getUsers(token),
        api.getPendingUsers(token).catch(() => []),
      ]);
      setUsers(allUsers.filter(u => u.is_active !== false));
      setPendingUsers(pending);
    } catch (error) {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchMerges();
  }, [token]);

  const handleApprove = async (userId, withRole = null) => {
    try {
      // Call API with role if provided
      const body = withRole ? { role: withRole } : {};
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/approve`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Error al aprobar usuario");
      }
      
      toast.success("Usuario aprobado correctamente. Se ha enviado un email de notificación.");
      setApproveDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.message || "Error al aprobar usuario");
    }
  };
  
  const openApproveDialog = (userToApprove) => {
    setSelectedUser(userToApprove);
    setApprovalRole(userToApprove.role || "analyst");
    setApproveDialogOpen(true);
  };

  const handleReject = async (userId) => {
    try {
      await api.rejectUser(token, userId);
      toast.success("Usuario rechazado");
      fetchUsers();
    } catch (error) {
      toast.error("Error al rechazar usuario");
    }
  };

  const handleChangeRole = async () => {
    if (!selectedUser || !newRole) return;
    try {
      await api.updateUserRole(token, selectedUser.id, newRole);
      toast.success("Rol actualizado correctamente");
      setRoleDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error("Error al cambiar rol");
    }
  };

  const handleToggleActive = async (userId, currentActive) => {
    try {
      await api.toggleUserActive(token, userId, !currentActive);
      toast.success(currentActive ? "Usuario desactivado" : "Usuario activado");
      fetchUsers();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  const handleSyncAirtable = async () => {
    try {
      setSyncStatus("syncing");
      const result = await api.syncAllFromAirtable(token);
      toast.success(`Sincronización completada: ${result.message || 'OK'}`);
      setSyncStatus("success");
    } catch (error) {
      toast.error("Error en sincronización");
      setSyncStatus("error");
    }
  };

  const handleOpenViewerConfig = async (viewerUser) => {
    setSelectedUser(viewerUser);
    try {
      const config = await api.getViewerConfig(token, viewerUser.id);
      if (config?.permissions) {
        setViewerPermissions(config.permissions);
      }
    } catch (e) {
      // Use defaults
    }
    setViewerDialogOpen(true);
  };

  const handleSaveViewerPermissions = async () => {
    if (!selectedUser) return;
    try {
      await api.updateViewerConfig(token, selectedUser.id, viewerPermissions);
      toast.success("Permisos de visualizador actualizados");
      setViewerDialogOpen(false);
    } catch (error) {
      toast.error("Error al guardar permisos");
    }
  };

  const togglePage = (pageId) => {
    setViewerPermissions(prev => ({
      ...prev,
      allowed_pages: prev.allowed_pages.includes(pageId)
        ? prev.allowed_pages.filter(p => p !== pageId)
        : [...prev.allowed_pages, pageId]
    }));
  };

  const filteredUsers = users.filter(u =>
    u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Shield className="w-5 h-5" />
              Acceso Restringido
            </CardTitle>
            <CardDescription>
              Solo los administradores pueden acceder a este panel.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Panel de Administración</h1>
          <p className="text-muted-foreground mt-1">Gestión de usuarios y configuración del sistema</p>
        </div>
        <Button onClick={handleSyncAirtable} disabled={syncStatus === "syncing"}>
          <RefreshCw className={`w-4 h-4 mr-2 ${syncStatus === "syncing" ? "animate-spin" : ""}`} />
          Sincronizar Airtable
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.length}</p>
                <p className="text-sm text-muted-foreground">Usuarios Activos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingUsers.length}</p>
                <p className="text-sm text-muted-foreground">Pendientes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-red-100 dark:bg-red-900/30">
                <Shield className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.role === 'admin').length}</p>
                <p className="text-sm text-muted-foreground">Administradores</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-100 dark:bg-green-900/30">
                <Activity className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{syncStatus === "success" ? "OK" : "—"}</p>
                <p className="text-sm text-muted-foreground">Sync Status</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending" className="gap-2">
            <Clock className="w-4 h-4" />
            Pendientes ({pendingUsers.length})
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-2">
            <Users className="w-4 h-4" />
            Todos los Usuarios
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="w-4 h-4" />
            Configuración
          </TabsTrigger>
          <TabsTrigger value="merges" className="gap-2" data-testid="admin-tab-merges">
            <GitMerge className="w-4 h-4" />
            Fusiones de Iniciativas ({initiativeMerges.length})
          </TabsTrigger>
        </TabsList>

        {/* Pending Users Tab */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Solicitudes Pendientes de Aprobación</CardTitle>
              <CardDescription>
                Revisa y aprueba o rechaza las solicitudes de acceso
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pendingUsers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No hay solicitudes pendientes</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Rol Solicitado</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingUsers.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">{u.full_name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Badge className={roleBadgeColors[u.role]}>
                            {roleLabels[u.role] || u.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(u.created_at).toLocaleDateString('es-ES')}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button size="sm" onClick={() => openApproveDialog(u)} data-testid={`approve-${u.id}`}>
                            <UserCheck className="w-4 h-4 mr-1" />
                            Aprobar
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleReject(u.id)}>
                            <UserX className="w-4 h-4 mr-1" />
                            Rechazar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Gestión de Usuarios</CardTitle>
                  <CardDescription>Administra roles y permisos de todos los usuarios</CardDescription>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar usuarios..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Registrado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{u.full_name}</p>
                          <p className="text-sm text-muted-foreground">{u.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={roleBadgeColors[u.role]}>
                          {roleLabels[u.role] || u.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={u.is_active !== false ? "default" : "secondary"}>
                          {u.is_active !== false ? "Activo" : "Inactivo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(u.created_at).toLocaleDateString('es-ES')}
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Select
                          value={u.role}
                          onValueChange={async (newRole) => {
                            try {
                              await api.updateUserRole(token, u.id, newRole);
                              toast.success("Rol actualizado");
                              fetchUsers();
                            } catch (e) {
                              toast.error("Error al cambiar rol");
                            }
                          }}
                        >
                          <SelectTrigger className="w-36">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(roleLabels).map(([key, label]) => (
                              <SelectItem key={key} value={key}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {u.role === 'viewer' && (
                          <Button size="sm" variant="outline" onClick={() => handleOpenViewerConfig(u)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Sincronización Airtable</CardTitle>
                <CardDescription>Configuración de sincronización automática</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Sync automático cada 6h</p>
                    <p className="text-sm text-muted-foreground">Importa datos de Airtable periódicamente</p>
                  </div>
                  <Badge variant="outline" className="bg-green-50 text-green-700">Activo</Badge>
                </div>
                <Button onClick={handleSyncAirtable} className="w-full" disabled={syncStatus === "syncing"}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncStatus === "syncing" ? "animate-spin" : ""}`} />
                  Sincronizar Ahora
                </Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Administradores</CardTitle>
                <CardDescription>Emails con acceso de administrador</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Shield className="w-4 h-4 text-red-600" />
                    <span className="font-mono text-sm">dredondo@valorizasm.com</span>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Shield className="w-4 h-4 text-red-600" />
                    <span className="font-mono text-sm">innovation@valorizasm.com</span>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Shield className="w-4 h-4 text-red-600" />
                    <span className="font-mono text-sm">demo@valoriza.com</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Viewer Role Configuration */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Configuración Rol Visualizador
                </CardTitle>
                <CardDescription>
                  Configura qué información puede ver el rol Visualizador para integración con intranet
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Selecciona un usuario con rol "Visualizador" en la pestaña de usuarios y haz clic en el icono 
                  <Eye className="w-4 h-4 inline mx-1" /> para configurar sus permisos individuales.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {allPages.map((page) => (
                    <div key={page.id} className="flex items-center gap-2 p-2 border rounded">
                      <span className="text-sm">{page.label}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Initiative Merges Tab */}
        <TabsContent value="merges">
          <Card data-testid="admin-merges-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GitMerge className="w-5 h-5" />
                Fusiones de Iniciativas Duplicadas
              </CardTitle>
              <CardDescription>
                El 2026-08-22 se fusionaron 37 iniciativas duplicadas (mismo nombre, censos/imports distintos) en 36 registros principales.
                Si alguna fusión no fue correcta, puedes restaurar la iniciativa archivada aquí.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {mergesLoading ? (
                <p className="text-sm text-muted-foreground">Cargando...</p>
              ) : initiativeMerges.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="admin-merges-empty">
                  No hay fusiones pendientes de revisión.
                </p>
              ) : (
                <Table data-testid="admin-merges-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Iniciativa archivada</TableHead>
                      <TableHead>Fusionada en</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead className="text-right">Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {initiativeMerges.map((m) => (
                      <TableRow key={m.id} data-testid={`admin-merge-row-${m.id}`}>
                        <TableCell className="font-medium">{m.name}</TableCell>
                        <TableCell className="text-muted-foreground">{m.merged_into_name}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {m._merged_at ? new Date(m._merged_at).toLocaleDateString("es-ES") : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1.5"
                            onClick={() => setRestoreTarget(m)}
                            data-testid={`restore-merge-btn-${m.id}`}
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                            Restaurar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      <ConfirmDialog
        open={!!restoreTarget}
        onOpenChange={(v) => !v && setRestoreTarget(null)}
        title="Restaurar iniciativa"
        description={`¿Restaurar "${restoreTarget?.name}"? Volverá a aparecer como iniciativa independiente en Portfolio.${restoreTarget?._redirected_refs && Object.keys(restoreTarget._redirected_refs).length > 0 ? "" : " Nota: si algún piloto u otro registro se re-vinculó durante la fusión original, puede que necesites re-asignarlo manualmente."}`}
        confirmLabel="Restaurar"
        destructive={false}
        onConfirm={confirmRestoreMerge}
        loading={restoring}
        testId="restore-merge-confirm-dialog"
      />

      {/* Approve User Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-green-600" />
              Aprobar Usuario
            </DialogTitle>
            <DialogDescription>
              Aprueba el acceso y asigna un rol a {selectedUser?.full_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-muted/50 rounded-lg space-y-2">
              <p className="text-sm"><strong>Nombre:</strong> {selectedUser?.full_name}</p>
              <p className="text-sm"><strong>Email:</strong> {selectedUser?.email}</p>
              <p className="text-sm"><strong>Fecha solicitud:</strong> {selectedUser?.created_at && new Date(selectedUser.created_at).toLocaleDateString('es-ES')}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Rol a asignar</label>
              <Select value={approvalRole} onValueChange={setApprovalRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="rd_lead">Líder I+D</SelectItem>
                  <SelectItem value="open_innovation_lead">Líder Open Innovation</SelectItem>
                  <SelectItem value="analyst">Analista</SelectItem>
                  <SelectItem value="stakeholder">Stakeholder</SelectItem>
                  <SelectItem value="internship">Internship</SelectItem>
                  <SelectItem value="viewer">Visualizador</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={() => handleApprove(selectedUser?.id, approvalRole)} className="bg-green-600 hover:bg-green-700">
              <UserCheck className="w-4 h-4 mr-2" />
              Aprobar y Asignar Rol
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Viewer Permissions Dialog */}
      <Dialog open={viewerDialogOpen} onOpenChange={setViewerDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Permisos de Visualizador
            </DialogTitle>
            <DialogDescription>
              Configura qué puede ver {selectedUser?.full_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div>
              <h4 className="font-medium mb-3">Páginas permitidas</h4>
              <div className="grid grid-cols-2 gap-2">
                {allPages.map((page) => (
                  <label key={page.id} className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-muted/50">
                    <Checkbox
                      checked={viewerPermissions.allowed_pages?.includes(page.id)}
                      onCheckedChange={() => togglePage(page.id)}
                    />
                    <span className="text-sm">{page.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium">Permisos adicionales</h4>
              <div className="space-y-2">
                <label className="flex items-center justify-between p-3 border rounded">
                  <span className="text-sm">Ver datos financieros</span>
                  <Switch
                    checked={viewerPermissions.can_view_financials}
                    onCheckedChange={(checked) => setViewerPermissions(p => ({...p, can_view_financials: checked}))}
                  />
                </label>
                <label className="flex items-center justify-between p-3 border rounded">
                  <span className="text-sm">Ver partners</span>
                  <Switch
                    checked={viewerPermissions.can_view_partners}
                    onCheckedChange={(checked) => setViewerPermissions(p => ({...p, can_view_partners: checked}))}
                  />
                </label>
                <label className="flex items-center justify-between p-3 border rounded">
                  <span className="text-sm">Ver KPIs</span>
                  <Switch
                    checked={viewerPermissions.can_view_kpis}
                    onCheckedChange={(checked) => setViewerPermissions(p => ({...p, can_view_kpis: checked}))}
                  />
                </label>
                <label className="flex items-center justify-between p-3 border rounded">
                  <span className="text-sm">Exportar datos</span>
                  <Switch
                    checked={viewerPermissions.can_export_data}
                    onCheckedChange={(checked) => setViewerPermissions(p => ({...p, can_export_data: checked}))}
                  />
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewerDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveViewerPermissions}>
              Guardar Permisos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
